// Confetti / particle burst — lightweight, no external libs.
// Emit a burst of colored particles at a fixed position (capture, home arrival, or game win).
// Each particle is a framer-motion div that animates outward + falls + fades.
import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

const PALETTE = ['#FFF200', '#E31E24', '#10b981', '#3b82f6', '#fbbf24', '#f472b6', '#ffffff'];

function rand(min, max) { return Math.random() * (max - min) + min; }

function makeBurst(count, scale = 1) {
    const arr = [];
    for (let i = 0; i < count; i++) {
        const angle = rand(0, Math.PI * 2);
        const dist = rand(60, 180) * scale;
        const x = Math.cos(angle) * dist;
        const y = Math.sin(angle) * dist - rand(20, 80) * scale; // bias upward
        const rotate = rand(-360, 360);
        arr.push({
            id: i, x, y, rotate,
            color: PALETTE[Math.floor(Math.random() * PALETTE.length)],
            size: rand(6, 12) * scale,
            shape: Math.random() < 0.4 ? 'circle' : Math.random() < 0.7 ? 'rect' : 'star',
            delay: rand(0, 0.05),
        });
    }
    return arr;
}

// Single-burst confetti at the centre of the parent element.
// Use `key` to retrigger (e.g. <Confetti key={burstCount} ... />).
export function ConfettiBurst({ count = 30, scale = 1, duration = 1.3 }) {
    const [pieces] = useState(() => makeBurst(count, scale));
    return (
        <div className="absolute inset-0 pointer-events-none flex items-center justify-center overflow-visible z-50" data-testid="ludo-confetti">
            {pieces.map((p) => (
                <motion.div
                    key={p.id}
                    initial={{ x: 0, y: 0, scale: 0, rotate: 0, opacity: 1 }}
                    animate={{
                        x: p.x,
                        y: [0, p.y, p.y + 180],
                        rotate: p.rotate,
                        scale: [0.4, 1, 1],
                        opacity: [1, 1, 0],
                    }}
                    transition={{ duration, ease: ['easeOut', 'easeIn'], delay: p.delay, times: [0, 0.6, 1] }}
                    className="absolute"
                    style={{
                        width: p.size,
                        height: p.size,
                        background: p.shape === 'star' ? 'transparent' : p.color,
                        borderRadius: p.shape === 'circle' ? '50%' : p.shape === 'rect' ? '2px' : '0',
                        boxShadow: `0 0 6px ${p.color}`,
                    }}
                >
                    {p.shape === 'star' && (
                        <svg viewBox="0 0 24 24" width={p.size} height={p.size}>
                            <polygon
                                points="12,2 15,9 22,9 17,14 19,21 12,17 5,21 7,14 2,9 9,9"
                                fill={p.color}
                            />
                        </svg>
                    )}
                </motion.div>
            ))}
        </div>
    );
}

// Repeated fireworks — fires successive bursts at random positions inside the parent.
// Used on game victory.
export function Fireworks({ duration = 4, density = 1.4 }) {
    const [shots, setShots] = useState([]);
    useEffect(() => {
        const end = Date.now() + duration * 1000;
        const interval = setInterval(() => {
            if (Date.now() > end) { clearInterval(interval); return; }
            setShots((s) => [
                ...s.slice(-10),
                {
                    id: Date.now() + Math.random(),
                    left: `${rand(8, 92)}%`,
                    top: `${rand(8, 70)}%`,
                    pieces: makeBurst(20, rand(0.7, 1.2)),
                },
            ]);
        }, 350 / density);
        return () => clearInterval(interval);
    }, [duration, density]);

    return (
        <div className="absolute inset-0 pointer-events-none z-40 overflow-hidden" data-testid="ludo-fireworks">
            <AnimatePresence>
                {shots.map((shot) => (
                    <div key={shot.id} className="absolute" style={{ left: shot.left, top: shot.top }}>
                        {shot.pieces.map((p) => (
                            <motion.div
                                key={p.id}
                                initial={{ x: 0, y: 0, scale: 0, opacity: 1 }}
                                animate={{ x: p.x * 0.6, y: p.y * 0.6 + 60, scale: [0.3, 1, 1], opacity: [1, 1, 0] }}
                                transition={{ duration: 1.2, ease: 'easeOut' }}
                                className="absolute"
                                style={{
                                    width: p.size,
                                    height: p.size,
                                    background: p.color,
                                    borderRadius: '50%',
                                    boxShadow: `0 0 8px ${p.color}`,
                                }}
                            />
                        ))}
                    </div>
                ))}
            </AnimatePresence>
        </div>
    );
}
